import { action, makeObservable, observable, runInAction } from "mobx";
import type { LoadedModel, Model } from "../models";
import apiService from "./apiService";
import type { PromiseCompletion } from "../classes/PromiseCompletion";

class ModelService {
  @observable activeModels: LoadedModel[] = [];
  @observable models: Model[] = [];
  @observable prefetchingModel: string | null = null;

  constructor() {
    makeObservable(this);
  }

  public loadActiveModels = async () => {
    const activeModels = await apiService.get<LoadedModel[]>('/tags/active');
    runInAction(() => {
      this.activeModels = activeModels;
    });
  }

  public loadModels = async (loader?: PromiseCompletion) => {
    const tags = await apiService.get<Model[]>('/tags/full', { loader: loader });

     runInAction(() => {
      this.models = tags;
      this.models.sort((a, b) => {
        // sort by isFavorite descending
        const aFavorite = a.isFavorite ? 1 : 0;
        const bFavorite = b.isFavorite ? 1 : 0;
        if (aFavorite !== bFavorite) return bFavorite - aFavorite;
        const serverCompare = a.server.localeCompare(b.server);
        if (serverCompare !== 0) return serverCompare;
        const aIndex = a.artificial_analysis_intelligence_index ?? Number.MAX_SAFE_INTEGER;
        const bIndex = b.artificial_analysis_intelligence_index ?? Number.MAX_SAFE_INTEGER;
        if (aIndex !== bIndex) return bIndex - aIndex;
        return this.getModelName(a).localeCompare(this.getModelName(b));
      });
    });
  };

  public getModelName(model: Model | string): string {
    if (typeof model === 'string') {
      const modelLoaded = this.models.find(m => m.name === model);
      if (!modelLoaded)
        return model;
      model = modelLoaded;
    }

    let modelName = model?.name || '';

    let parameter_size = model?.parameter_size ?
      model.parameter_size.replace(/^(\d+(?:\.\d+)?)(B)$/, (_, p1, p2) =>
        Math.round(parseFloat(p1) - 0.01) + p2
      )
      : '';

    // if modelName contains parameter size like -30B, -7b etc, use it as parameter_size and remove from modelName
    const paramSizeMatch = modelName.match(/-(\d+(?:\.\d+)?[bB])/);
    if (paramSizeMatch) {
      const sizeStr = paramSizeMatch[1];
      modelName = modelName.replace(`-${sizeStr}`, '');
      parameter_size = sizeStr.toUpperCase();
    }

    //remove quantization level from model name (q8_0, q4_0, q4_1, q4_k_m etc)
    modelName = modelName.replace(/-?q\d+((_\d+)?(_K_M)?(_k_m)?)/, '');

    // remove active parameters size like a3b or a11B
    modelName = modelName.replace(/-?[aA]\d+[bB]/, '');

    if (model && modelName.endsWith(':latest')) {
      modelName = modelName.replace(':latest', `:${parameter_size}`);
    }
    if (model && modelName.endsWith(model.quantization_level)) {
      modelName = modelName.replace(model.quantization_level, `${parameter_size}`);
    }

    modelName = modelName.replace(/[-_]?GGUF/i, '');
    modelName = modelName.replace(/[-_]?MXFP4/i, '');
    modelName = modelName.replace(/[-_]?MOE/i, '');
    modelName = modelName.replace(/[-_]?instruct/i, '');
    modelName = modelName.replace(/[-_]?tainted/i, '');
    modelName = modelName.replace(/[-_]?BF16/i, '');
    modelName = modelName.replace(/kldzj_/i, '');

    if (modelName && modelName.includes('/')) {
      modelName = modelName.split('/').pop() || modelName;
    }

    const modelParts = modelName.split(':');
    let [baseName, size] = modelParts;
    baseName = baseName.replace(/[-_\.]+$/g, '');
    modelName = size ? `${baseName}:${size.toUpperCase()}` : `${baseName}:${parameter_size}`;

    return modelName;
  }

  @action
  public async prefetchModel(model: Model) {
    const activeModels = await apiService.get<LoadedModel[]>('/tags/active');
    const activeModel = activeModels.find(m => m.name === model.name && m.server === model.server);
    if (activeModel) return;

    this.prefetchingModel = model.name;
    try {
      await apiService.post('/tags/prefetch/' + encodeURIComponent(model.name), null, {
        headers: {
          'Content-Type': 'application/json',
          'x-ollama-server': model.server
        }
      });
    } finally {
      runInAction(() => {
        this.prefetchingModel = null;
      });
    }

    await this.loadActiveModels();
  }
}

export default new ModelService();
